/**
 * ---------------------------------------------------------------------------
 * Task workflow engine (declarative)
 * ---------------------------------------------------------------------------
 * States and transitions are DATA. Adding a state or a transition must never
 * require editing a route handler, a component or a query. Everything that
 * needs to know "what can happen next" derives it from `TASK_TRANSITIONS`.
 */
export const TASK_STATUSES = [
    'NEW',
    'CLAIMED',
    'IN_PROGRESS',
    'BLOCKED',
    'AWAITING_INFORMATION',
    'COMPLETED',
    'PENDING_FINAL_REVIEW',
    'CHANGES_REQUESTED',
    'APPROVED',
    'CANCELLED',
    'ARCHIVED',
];
export function isTaskStatus(value) {
    return typeof value === 'string' && TASK_STATUSES.includes(value);
}
export const TASK_STATUS_META = {
    NEW: {
        key: 'NEW',
        label: 'New',
        shortLabel: 'New',
        description: 'Task has been created and is waiting to be picked up.',
        group: 'OPEN',
        tone: 'neutral',
        icon: 'circle-dashed',
        order: 10,
        isTerminal: false,
        countsAsActive: true,
        isAwaitingReview: false,
        countsAsWork: false,
        pausesWorkClock: false,
        exposedByDefault: true,
    },
    CLAIMED: {
        key: 'CLAIMED',
        label: 'Claimed',
        shortLabel: 'Claimed',
        description: 'The assigned developer has acknowledged ownership of the task.',
        group: 'OPEN',
        tone: 'info',
        icon: 'hand',
        order: 20,
        isTerminal: false,
        countsAsActive: true,
        isAwaitingReview: false,
        countsAsWork: false,
        pausesWorkClock: false,
        exposedByDefault: true,
    },
    IN_PROGRESS: {
        key: 'IN_PROGRESS',
        label: 'In Progress',
        shortLabel: 'In Progress',
        description: 'Active development work is underway.',
        group: 'ACTIVE',
        tone: 'active',
        icon: 'play',
        order: 30,
        isTerminal: false,
        countsAsActive: true,
        isAwaitingReview: false,
        countsAsWork: true,
        pausesWorkClock: false,
        exposedByDefault: true,
    },
    BLOCKED: {
        key: 'BLOCKED',
        label: 'Blocked',
        shortLabel: 'Blocked',
        description: 'Work cannot continue because of an external dependency.',
        group: 'ACTIVE',
        tone: 'warning',
        icon: 'octagon-alert',
        order: 40,
        isTerminal: false,
        countsAsActive: true,
        isAwaitingReview: false,
        countsAsWork: false,
        pausesWorkClock: true,
        exposedByDefault: false,
    },
    AWAITING_INFORMATION: {
        key: 'AWAITING_INFORMATION',
        label: 'Awaiting Information',
        shortLabel: 'Awaiting Info',
        description: 'Waiting on clarification before work can continue.',
        group: 'ACTIVE',
        tone: 'warning',
        icon: 'message-circle-question',
        order: 50,
        isTerminal: false,
        countsAsActive: true,
        isAwaitingReview: false,
        countsAsWork: false,
        pausesWorkClock: true,
        exposedByDefault: false,
    },
    COMPLETED: {
        key: 'COMPLETED',
        label: 'Completed',
        shortLabel: 'Completed',
        description: 'The developer considers implementation finished. This is not final approval.',
        group: 'REVIEW',
        tone: 'positive',
        icon: 'check',
        order: 60,
        isTerminal: false,
        countsAsActive: true,
        isAwaitingReview: false,
        countsAsWork: false,
        pausesWorkClock: false,
        exposedByDefault: true,
    },
    PENDING_FINAL_REVIEW: {
        key: 'PENDING_FINAL_REVIEW',
        label: 'Pending Final Review',
        shortLabel: 'In Review',
        description: 'Submitted to the Development Directors for final review.',
        group: 'REVIEW',
        tone: 'attention',
        icon: 'gavel',
        order: 70,
        isTerminal: false,
        countsAsActive: true,
        isAwaitingReview: true,
        countsAsWork: false,
        // Waiting on a director is not developer working time and is never counted
        // as such — it is reported separately as "awaiting review". Marking it as a
        // pause as well would subtract it twice.
        pausesWorkClock: false,
        exposedByDefault: true,
    },
    CHANGES_REQUESTED: {
        key: 'CHANGES_REQUESTED',
        label: 'Changes Requested',
        shortLabel: 'Changes',
        description: 'A director returned the task to the developer with required changes.',
        group: 'ACTIVE',
        tone: 'warning',
        icon: 'undo-2',
        order: 80,
        isTerminal: false,
        countsAsActive: true,
        isAwaitingReview: false,
        countsAsWork: false,
        pausesWorkClock: false,
        exposedByDefault: true,
    },
    APPROVED: {
        key: 'APPROVED',
        label: 'Approved',
        shortLabel: 'Approved',
        description: 'Signed off by a Development Director. The task is done.',
        group: 'CLOSED',
        tone: 'success',
        icon: 'badge-check',
        order: 90,
        isTerminal: true,
        countsAsActive: false,
        isAwaitingReview: false,
        countsAsWork: false,
        pausesWorkClock: false,
        exposedByDefault: true,
    },
    CANCELLED: {
        key: 'CANCELLED',
        label: 'Cancelled',
        shortLabel: 'Cancelled',
        description: 'The task will not be delivered. Retained for the record.',
        group: 'CLOSED',
        tone: 'muted',
        icon: 'circle-slash',
        order: 100,
        isTerminal: true,
        countsAsActive: false,
        isAwaitingReview: false,
        countsAsWork: false,
        pausesWorkClock: false,
        exposedByDefault: true,
    },
    ARCHIVED: {
        key: 'ARCHIVED',
        label: 'Archived',
        shortLabel: 'Archived',
        description: 'Closed and moved out of the active record set.',
        group: 'CLOSED',
        tone: 'muted',
        icon: 'archive',
        order: 110,
        isTerminal: true,
        countsAsActive: false,
        isAwaitingReview: false,
        countsAsWork: false,
        pausesWorkClock: false,
        exposedByDefault: true,
    },
};
/** The happy-path pipeline rendered as the status timeline on a task (§23). */
export const PRIMARY_PIPELINE = [
    'NEW',
    'CLAIMED',
    'IN_PROGRESS',
    'COMPLETED',
    'PENDING_FINAL_REVIEW',
    'APPROVED',
];
export const ACTIVE_STATUSES = TASK_STATUSES.filter((s) => TASK_STATUS_META[s].countsAsActive);
export const CLOSED_STATUSES = TASK_STATUSES.filter((s) => TASK_STATUS_META[s].group === 'CLOSED');
/**
 * Every action a user can take to move a task. `action` is the verb sent by the
 * client — clients never post a raw target status, which keeps the transition
 * graph authoritative on the server.
 */
export const TASK_ACTIONS = [
    'CLAIM',
    'START',
    'BLOCK',
    'AWAIT_INFORMATION',
    'RESUME',
    'COMPLETE',
    'SUBMIT_FOR_REVIEW',
    'REOPEN',
    'APPROVE',
    'REQUEST_CHANGES',
    'ADDRESS_CHANGES',
    'CANCEL',
    'ARCHIVE',
];
export function isTaskAction(value) {
    return typeof value === 'string' && TASK_ACTIONS.includes(value);
}
export const TASK_TRANSITIONS = [
    {
        action: 'CLAIM',
        from: ['NEW'],
        to: 'CLAIMED',
        label: 'Claim Task',
        description: 'Acknowledge ownership of this task.',
        ownPermission: 'task:transition:own',
        anyPermission: 'task:transition:any',
        scope: 'ASSIGNEE',
        requiresComment: false,
        stamps: ['claimedAt'],
        intent: 'primary',
        exposedByDefault: true,
    },
    {
        action: 'START',
        from: ['CLAIMED'],
        to: 'IN_PROGRESS',
        label: 'Start Work',
        description: 'Begin active development on this task.',
        ownPermission: 'task:transition:own',
        anyPermission: 'task:transition:any',
        scope: 'ASSIGNEE',
        requiresComment: false,
        stamps: ['startedAt'],
        intent: 'primary',
        exposedByDefault: true,
    },
    {
        action: 'BLOCK',
        from: ['CLAIMED', 'IN_PROGRESS'],
        to: 'BLOCKED',
        label: 'Mark Blocked',
        description: 'Work cannot continue. Time spent blocked is excluded from your cycle time.',
        ownPermission: 'task:transition:own',
        anyPermission: 'task:transition:any',
        scope: 'ASSIGNEE',
        requiresComment: true,
        stamps: [],
        intent: 'secondary',
        exposedByDefault: false,
    },
    {
        action: 'AWAIT_INFORMATION',
        from: ['CLAIMED', 'IN_PROGRESS'],
        to: 'AWAITING_INFORMATION',
        label: 'Request Information',
        description: 'Pause pending clarification. Waiting time is excluded from your cycle time.',
        ownPermission: 'task:transition:own',
        anyPermission: 'task:transition:any',
        scope: 'ASSIGNEE',
        requiresComment: true,
        stamps: [],
        intent: 'secondary',
        exposedByDefault: false,
    },
    {
        action: 'RESUME',
        from: ['BLOCKED', 'AWAITING_INFORMATION'],
        to: 'IN_PROGRESS',
        label: 'Resume Work',
        description: 'The blocker is cleared — continue development.',
        ownPermission: 'task:transition:own',
        anyPermission: 'task:transition:any',
        scope: 'ASSIGNEE',
        requiresComment: false,
        stamps: [],
        stampsIfEmpty: ['startedAt'],
        intent: 'primary',
        exposedByDefault: false,
    },
    {
        action: 'COMPLETE',
        from: ['IN_PROGRESS'],
        to: 'COMPLETED',
        label: 'Mark Completed',
        description: 'Declare the implementation finished. This is not final approval.',
        ownPermission: 'task:transition:own',
        anyPermission: 'task:transition:any',
        scope: 'ASSIGNEE',
        requiresComment: false,
        stamps: ['completedAt'],
        intent: 'positive',
        exposedByDefault: true,
    },
    {
        action: 'SUBMIT_FOR_REVIEW',
        from: ['COMPLETED'],
        to: 'PENDING_FINAL_REVIEW',
        label: 'Submit For Final Review',
        description: 'Send this task to the Development Directors for sign-off.',
        ownPermission: 'task:transition:own',
        anyPermission: 'task:transition:any',
        scope: 'ASSIGNEE',
        requiresComment: false,
        stamps: ['submittedForReviewAt'],
        intent: 'primary',
        exposedByDefault: true,
        startsReviewCycle: true,
    },
    {
        action: 'REOPEN',
        from: ['COMPLETED'],
        to: 'IN_PROGRESS',
        label: 'Reopen',
        description: 'Return the task to active development before it was submitted.',
        ownPermission: 'task:transition:own',
        anyPermission: 'task:transition:any',
        scope: 'ASSIGNEE',
        requiresComment: true,
        stamps: [],
        intent: 'secondary',
        exposedByDefault: true,
        countsAsReopen: true,
    },
    {
        action: 'APPROVE',
        from: ['PENDING_FINAL_REVIEW'],
        to: 'APPROVED',
        label: 'Approve',
        description: 'Sign off this task as complete and accepted.',
        ownPermission: null,
        anyPermission: 'review:approve',
        scope: 'ANY',
        requiresComment: false,
        stamps: ['finalReviewAt', 'approvedAt'],
        intent: 'positive',
        exposedByDefault: true,
    },
    {
        action: 'REQUEST_CHANGES',
        from: ['PENDING_FINAL_REVIEW'],
        to: 'CHANGES_REQUESTED',
        label: 'Request Changes',
        description: 'Return the task to the developer with the changes you require.',
        ownPermission: null,
        anyPermission: 'review:request_changes',
        scope: 'ANY',
        requiresComment: true,
        stamps: ['finalReviewAt'],
        intent: 'danger',
        exposedByDefault: true,
    },
    {
        action: 'ADDRESS_CHANGES',
        from: ['CHANGES_REQUESTED'],
        to: 'IN_PROGRESS',
        label: 'Address Changes',
        description: 'Pick the task back up and work through the requested changes.',
        ownPermission: 'task:transition:own',
        anyPermission: 'task:transition:any',
        scope: 'ASSIGNEE',
        requiresComment: false,
        stamps: [],
        stampsIfEmpty: ['startedAt'],
        intent: 'primary',
        exposedByDefault: true,
        countsAsReopen: true,
    },
    {
        action: 'CANCEL',
        from: [
            'NEW',
            'CLAIMED',
            'IN_PROGRESS',
            'BLOCKED',
            'AWAITING_INFORMATION',
            'COMPLETED',
            'PENDING_FINAL_REVIEW',
            'CHANGES_REQUESTED',
        ],
        to: 'CANCELLED',
        label: 'Cancel Task',
        description: 'Stop this task permanently. The full record is retained.',
        ownPermission: null,
        anyPermission: 'task:cancel',
        scope: 'ANY',
        requiresComment: true,
        stamps: ['cancelledAt'],
        intent: 'danger',
        exposedByDefault: true,
    },
    {
        action: 'ARCHIVE',
        from: ['APPROVED', 'CANCELLED'],
        to: 'ARCHIVED',
        label: 'Archive',
        description: 'Move this closed task out of the active record set.',
        ownPermission: null,
        anyPermission: 'task:archive',
        scope: 'ANY',
        requiresComment: false,
        stamps: ['archivedAt'],
        intent: 'secondary',
        exposedByDefault: true,
    },
];
const TRANSITION_BY_ACTION = new Map(TASK_TRANSITIONS.map((t) => [t.action, t]));
export function getTransition(action) {
    return TRANSITION_BY_ACTION.get(action);
}
/** All transitions leaving `status`, irrespective of who the actor is. */
export function transitionsFrom(status) {
    return TASK_TRANSITIONS.filter((t) => t.from.includes(status));
}
/**
 * Single authority for "may this actor run this action on this task right now".
 * The server calls it before mutating; the web client calls it to decide which
 * buttons to render. Identical logic, one implementation.
 */
export function canTransition(actor, subject, action, options = {}) {
    const transition = TRANSITION_BY_ACTION.get(action);
    if (!transition)
        return { allowed: false, reason: 'UNKNOWN_ACTION' };
    if (!transition.from.includes(subject.status)) {
        return { allowed: false, reason: 'INVALID_FROM_STATUS', transition };
    }
    const extendedEnabled = options.extendedStatesEnabled ?? false;
    const targetExposed = TASK_STATUS_META[transition.to].exposedByDefault && transition.exposedByDefault;
    if (!targetExposed && !extendedEnabled) {
        return { allowed: false, reason: 'NOT_EXPOSED', transition };
    }
    const hasAny = actor.permissions.includes(transition.anyPermission);
    const isOwner = subject.assigneeId !== null && subject.assigneeId === actor.userId;
    const hasOwn = transition.scope === 'ASSIGNEE' &&
        isOwner &&
        transition.ownPermission !== null &&
        actor.permissions.includes(transition.ownPermission);
    if (!hasAny && !hasOwn) {
        return { allowed: false, reason: 'NOT_PERMITTED', transition };
    }
    if (transition.requiresComment && !options.comment?.trim()) {
        return { allowed: false, reason: 'COMMENT_REQUIRED', transition };
    }
    return { allowed: true, transition };
}
/**
 * The actions this actor can currently perform. Used to render the task action
 * bar; the server re-checks every one of them on submission.
 */
export function availableTransitions(actor, subject, options = {}) {
    return transitionsFrom(subject.status).filter((t) => {
        const check = canTransition(actor, subject, t.action, {
            ...options,
            // Ignore the comment requirement here: the UI collects the comment in the
            // action dialog, so the button must still be offered.
            comment: t.requiresComment ? 'placeholder' : null,
        });
        return check.allowed;
    });
}
export const TRANSITION_DENIAL_MESSAGES = {
    UNKNOWN_ACTION: 'That workflow action does not exist.',
    INVALID_FROM_STATUS: 'This task is not in a state where that action is valid.',
    NOT_PERMITTED: 'You do not have permission to perform that action on this task.',
    NOT_EXPOSED: 'That workflow state is not enabled on this platform.',
    COMMENT_REQUIRED: 'A comment is required for this action.',
};
//# sourceMappingURL=workflow.js.map